import { ApiResponse } from '@/types';
import { api } from './api';

export interface DropdownOption {
  value: string;
  label: string;
  metadata?: Record<string, any>;
  isActive: boolean;
  order: number;
}

export interface ProjectTool {
  id: string;
  name: string;
  description: string;
  category: 'planning' | 'design' | 'construction' | 'management' | 'safety' | 'quality';
  isRequired: boolean;
  isActive: boolean;
  order: number;
  configuration?: Record<string, any>;
}

export interface ProjectDirectory {
  id: string;
  name: string;
  description: string;
  tags: string[];
  parentId?: string;
  isDefault: boolean;
  isActive: boolean;
  order: number;
}

export interface Configuration {
  _id: string;
  key: string;
  type: 'dropdown' | 'tools' | 'directories' | 'settings';
  category: string;
  name: string;
  description?: string;
  options?: DropdownOption[];
  tools?: ProjectTool[];
  directories?: ProjectDirectory[];
  settings?: Record<string, any>;
  isActive: boolean;
  isSystem: boolean; // System configurations cannot be deleted
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

class ConfigurationService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Get dropdown options by key with caching
   */
  async getDropdownOptions(key: string): Promise<DropdownOption[]> {
    const cacheKey = `dropdown_${key}`;
    const cached = this.getFromCache(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const response = await api.get<ApiResponse<DropdownOption[]>>(
        `/configurations/dropdown/${key}`
      );

      if (response.data.success && response.data.data) {
        this.setCache(cacheKey, response.data.data);
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error(`Failed to fetch dropdown options for ${key}:`, error);
      return [];
    }
  }

  /**
   * Get project tools with caching
   */
  async getProjectTools(): Promise<ProjectTool[]> {
    const cacheKey = 'project_tools';
    const cached = this.getFromCache(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const response = await api.get<ApiResponse<ProjectTool[]>>('/configurations/tools');

      if (response.data.success && response.data.data) {
        this.setCache(cacheKey, response.data.data);
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch project tools:', error);
      return [];
    }
  }

  /**
   * Get project directories with caching
   */
  async getProjectDirectories(): Promise<ProjectDirectory[]> {
    const cacheKey = 'project_directories';
    const cached = this.getFromCache(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const response = await api.get<ApiResponse<ProjectDirectory[]>>(
        '/configurations/directories'
      );

      if (response.data.success && response.data.data) {
        this.setCache(cacheKey, response.data.data);
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch project directories:', error);
      return [];
    }
  }

  /**
   * Get setting value by key
   */
  async getSetting(key: string, defaultValue?: any): Promise<any> {
    const cacheKey = `setting_${key}`;
    const cached = this.getFromCache(cacheKey);

    if (cached !== undefined) {
      return cached;
    }

    try {
      const response = await api.get<ApiResponse<any>>(`/configurations/setting/${key}`);

      if (response.data.success && response.data.data !== undefined) {
        this.setCache(cacheKey, response.data.data);
        return response.data.data;
      }

      return defaultValue;
    } catch (error) {
      console.error(`Failed to fetch setting for ${key}:`, error);
      return defaultValue;
    }
  }

  /**
   * Get multiple dropdown options at once
   */
  async getMultipleDropdowns(keys: string[]): Promise<Record<string, DropdownOption[]>> {
    const results: Record<string, DropdownOption[]> = {};

    // Check cache first
    const uncachedKeys: string[] = [];
    for (const key of keys) {
      const cacheKey = `dropdown_${key}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        results[key] = cached;
      } else {
        uncachedKeys.push(key);
      }
    }

    // Fetch uncached keys
    if (uncachedKeys.length > 0) {
      try {
        const response = await api.post<ApiResponse<Record<string, DropdownOption[]>>>(
          '/configurations/dropdowns/bulk',
          {
            keys: uncachedKeys,
          }
        );

        if (response.data.success && response.data.data) {
          Object.entries(response.data.data).forEach(([key, options]) => {
            results[key] = options;
            this.setCache(`dropdown_${key}`, options);
          });
        }
      } catch (error) {
        console.error('Failed to fetch multiple dropdown options:', error);
        // Set empty arrays for failed keys
        uncachedKeys.forEach(key => {
          results[key] = [];
        });
      }
    }

    return results;
  }

  /**
   * Get all project creation configurations at once
   */
  async getProjectCreationConfig(): Promise<{
    countries: DropdownOption[];
    states: DropdownOption[];
    timezones: DropdownOption[];
    regions: DropdownOption[];
    languages: DropdownOption[];
    currencies: DropdownOption[];
    specificationCategories: DropdownOption[];
    specificationPriorities: DropdownOption[];
    drawingCategories: DropdownOption[];
    tools: ProjectTool[];
    directories: ProjectDirectory[];
  }> {
    try {
      const [dropdowns, tools, directories] = await Promise.all([
        this.getMultipleDropdowns([
          'countries',
          'states',
          'timezones',
          'regions',
          'languages',
          'currencies',
          'specification-categories',
          'specification-priorities',
          'drawing-categories',
        ]),
        this.getProjectTools(),
        this.getProjectDirectories(),
      ]);

      return {
        countries: dropdowns.countries || [],
        states: dropdowns.states || [],
        timezones: dropdowns.timezones || [],
        regions: dropdowns.regions || [],
        languages: dropdowns.languages || [],
        currencies: dropdowns.currencies || [],
        specificationCategories: dropdowns['specification-categories'] || [],
        specificationPriorities: dropdowns['specification-priorities'] || [],
        drawingCategories: dropdowns['drawing-categories'] || [],
        tools,
        directories,
      };
    } catch (error) {
      console.error('Failed to fetch project creation config:', error);
      return {
        countries: [],
        states: [],
        timezones: [],
        regions: [],
        languages: [],
        currencies: [],
        specificationCategories: [],
        specificationPriorities: [],
        drawingCategories: [],
        tools: [],
        directories: [],
      };
    }
  }

  /**
   * Invalidate cache for specific key or all cache
   */
  invalidateCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get data from cache if not expired
   */
  private getFromCache(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  /**
   * Set data in cache with timestamp
   */
  private setCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Preload common configurations for better performance
   */
  async preloadConfigurations(): Promise<void> {
    try {
      const commonKeys = [
        'countries',
        'states',
        'timezones',
        'regions',
        'languages',
        'currencies',
        'team-roles',
        'project-statuses',
        'task-priorities',
        'task-statuses',
        'meeting-types',
        'document-categories',
        'file-types',
        'specification-categories',
        'specification-priorities',
        'drawing-categories',
      ];

      const promises = commonKeys.map(key => this.getDropdownOptions(key));
      await Promise.allSettled(promises);

      // Also preload tools and directories
      await Promise.allSettled([this.getProjectTools(), this.getProjectDirectories()]);
    } catch (error) {
      console.error('Failed to preload configurations:', error);
    }
  }
}

// Export singleton instance
export const configurationService = new ConfigurationService();
